import * as Headless from '@headlessui/react';
import {
  BuildingOffice2Icon,
  CalendarIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import React from 'react';
import {
  AdminAnalyticsSegmentFilter,
  type GetChildAgenciesQuery,
  useGetAgencyUsersQuery,
  useGetChildAgenciesQuery
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { utcDateFormatter } from '~/components/settings/analytics/analytics-constants';
import { SEGMENT_FILTER_NAMES } from '~/pages/settings/analytics-segment-filters';
import { AnalyticsAssumptionsPicker } from './analytics-assumptions-picker';
import { AnalyticsExportMenu } from './analytics-export-menu';
import {
  AnalyticsFilterPicker,
  FILTER_PICKER_PANEL_CLASS,
  FILTER_PICKER_TRIGGER_CLASS
} from './analytics-filter-picker';
import {
  ProfilePicker,
  SegmentFilterChips,
  StartedByPicker
} from './analytics-segment-filter-pickers';
import {
  type AnalyticsPeriod,
  type AnalyticsViewState,
  effectiveSubagencyScope,
  parseISODate,
  type SubagencyScope,
  type useAnalyticsFilters
} from './use-analytics-filters';

type ChildAgency = NonNullable<
  NonNullable<GetChildAgenciesQuery['agencyUser']>['agency']['childAgencies'][number]
>;

const PERIOD_OPTIONS: { value: AnalyticsPeriod; label: string }[] = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'lastYear', label: 'Last year' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' }
];

function isTestUserEmail(email: string): boolean {
  return /.+@getcara\.ai$/i.test(email) && email.includes('+');
}

function periodSummary(state: AnalyticsViewState): string {
  if (state.period === 'custom') {
    const start = state.customAfter ? parseISODate(state.customAfter) : null;
    const end = state.customBefore ? parseISODate(state.customBefore) : null;
    if (start && end) {
      return `${utcDateFormatter.format(start)} – ${utcDateFormatter.format(end)}`;
    }
    if (start) return `Since ${utcDateFormatter.format(start)}`;
    if (end) return `Through ${utcDateFormatter.format(end)}`;
    return 'Custom range';
  }
  return PERIOD_OPTIONS.find((opt) => opt.value === state.period)?.label ?? 'Time period';
}

const TimePeriodPicker = ({
  viewState,
  customRangeError,
  setPeriod,
  setCustomAfter,
  setCustomBefore
}: Pick<
  ReturnType<typeof useAnalyticsFilters>,
  'viewState' | 'customRangeError' | 'setPeriod' | 'setCustomAfter' | 'setCustomBefore'
>) => {
  const isFiltered = viewState.period !== 'all';

  return (
    <Headless.Popover className="relative">
      {({ close }) => (
        <>
          <Headless.PopoverButton
            className={FILTER_PICKER_TRIGGER_CLASS}
            data-active={isFiltered ? '' : undefined}
            data-filter-trigger="Date"
          >
            <CalendarIcon className="size-4" />
            <span>{periodSummary(viewState)}</span>
            <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
          </Headless.PopoverButton>
          <Headless.PopoverPanel
            transition
            anchor="bottom start"
            className={FILTER_PICKER_PANEL_CLASS}
          >
            <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Time period
            </div>
            <div className="flex flex-col">
              {PERIOD_OPTIONS.map((opt) => {
                const selected = viewState.period === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setPeriod(opt.value);
                      if (opt.value !== 'custom') {
                        close();
                      }
                    }}
                    className={clsx(
                      'flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
                      selected
                        ? 'bg-primary-50 dark:bg-primary-500/10 font-medium text-primary-900 dark:text-primary-200'
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                    )}
                  >
                    <span>{opt.label}</span>
                    {selected && (
                      <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                );
              })}
            </div>
            {viewState.period === 'custom' && (
              <div className="mt-2 border-t border-neutral-100 dark:border-neutral-700 pt-3">
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-400">
                    <span>From</span>
                    <input
                      type="date"
                      value={viewState.customAfter ?? ''}
                      onChange={(e) => setCustomAfter(e.target.value)}
                      max={viewState.customBefore || undefined}
                      className="rounded-md border border-neutral-300 dark:border-neutral-600 px-2 py-1 text-xs text-neutral-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-neutral-600 dark:text-neutral-400">
                    <span>To</span>
                    <input
                      type="date"
                      value={viewState.customBefore ?? ''}
                      onChange={(e) => setCustomBefore(e.target.value)}
                      min={viewState.customAfter || undefined}
                      className="rounded-md border border-neutral-300 dark:border-neutral-600 px-2 py-1 text-xs text-neutral-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                </div>
                {customRangeError && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    {customRangeError}
                  </p>
                )}
              </div>
            )}
          </Headless.PopoverPanel>
        </>
      )}
    </Headless.Popover>
  );
};

const THIS_AGENCY_LABEL = 'This agency';
const ALL_SUBAGENCIES_LABEL = 'All subagencies';
const ALL_TEAM_LABEL = 'All team';
const ROLLUP_TEAM_TITLE = 'Team member filter unavailable in rollup view';

function subagencyScopeLabel(scope: SubagencyScope, childAgencies: ChildAgency[]): string {
  if (scope.kind === 'all') return ALL_SUBAGENCIES_LABEL;
  if (scope.kind === 'child') {
    return childAgencies.find((a) => a.id === scope.agencyId)?.name ?? 'Subagency';
  }
  return THIS_AGENCY_LABEL;
}

const SubagencyPicker = ({
  childAgencies,
  viewState,
  setSubagencyScope
}: {
  childAgencies: ChildAgency[];
} & Pick<ReturnType<typeof useAnalyticsFilters>, 'viewState' | 'setSubagencyScope'>) => {
  const scope = effectiveSubagencyScope(viewState);
  const scopes: SubagencyScope[] = [
    { kind: 'parent' },
    { kind: 'all' },
    ...childAgencies.map((agency) => ({ kind: 'child' as const, agencyId: agency.id }))
  ];
  const scopeKey = (option: SubagencyScope) =>
    option.kind === 'child' ? option.agencyId : option.kind;

  return (
    <AnalyticsFilterPicker
      icon={<BuildingOffice2Icon className="size-4" />}
      label={subagencyScopeLabel(scope, childAgencies)}
      heading="Agency"
      isActive={scope.kind !== 'parent'}
      selectedKey={scopeKey(scope)}
      options={scopes.map((option) => ({
        key: scopeKey(option),
        label: subagencyScopeLabel(option, childAgencies),
        onSelect: () => setSubagencyScope(option)
      }))}
    />
  );
};

type AnalyticsAgencyUser = { id: string; email: string; name: string };

// Stable, so the agency user memo isn't recomputed on every render while child agencies load.
const NO_CHILD_AGENCIES: ChildAgency[] = [];

/**
 * The agency users in scope, test accounts left out, sorted by name. Once they load, clears a
 * picked user the list lacks, such as a deleted user, which would otherwise stay applied with no
 * chip to remove it.
 */
function useAnalyticsAgencyUsers(
  scope: SubagencyScope,
  childAgencies: ChildAgency[],
  agencyUserId: string | undefined,
  setAgencyUserId: (agencyUserId: string | undefined) => void
): AnalyticsAgencyUser[] {
  const childAgencyId = scope.kind === 'child' ? scope.agencyId : undefined;
  const { data: usersData } = useGetAgencyUsersQuery({ skip: childAgencyId != null });
  const loadedUsers: AnalyticsAgencyUser[] | undefined =
    childAgencyId != null
      ? childAgencies.find((a) => a.id === childAgencyId)?.users
      : usersData?.agencyUsers;
  const agencyUsers = React.useMemo(
    () =>
      (loadedUsers ?? [])
        .filter((u) => !isTestUserEmail(u.email))
        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [loadedUsers]
  );

  const isStaleSelection =
    agencyUserId != null &&
    loadedUsers != null &&
    !agencyUsers.some((user) => user.id === agencyUserId);
  React.useEffect(() => {
    if (isStaleSelection) setAgencyUserId(undefined);
  }, [isStaleSelection, setAgencyUserId]);

  return agencyUsers;
}

const TeamMemberPicker = ({
  agencyUsers,
  viewState,
  setAgencyUserId
}: {
  agencyUsers: AnalyticsAgencyUser[];
} & Pick<ReturnType<typeof useAnalyticsFilters>, 'viewState' | 'setAgencyUserId'>) => {
  const scope = effectiveSubagencyScope(viewState);
  const rollupMode = scope.kind === 'all';
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agencyUsers;
    return agencyUsers.filter(
      (u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
    );
  }, [agencyUsers, query]);

  const selectedUser = viewState.agencyUserId
    ? agencyUsers.find((u) => u.id === viewState.agencyUserId)
    : undefined;
  const triggerLabel = selectedUser?.name || selectedUser?.email || ALL_TEAM_LABEL;
  const isFiltered = !!viewState.agencyUserId && !rollupMode;

  if (rollupMode) {
    return (
      <span title={ROLLUP_TEAM_TITLE}>
        <button
          type="button"
          disabled
          className={clsx(FILTER_PICKER_TRIGGER_CLASS, 'cursor-not-allowed opacity-60')}
        >
          <UserIcon className="size-4" />
          <span className="max-w-[12rem] truncate">{ALL_TEAM_LABEL}</span>
          <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
        </button>
      </span>
    );
  }

  return (
    <Headless.Popover className="relative">
      {({ close }) => (
        <>
          <Headless.PopoverButton
            className={FILTER_PICKER_TRIGGER_CLASS}
            data-active={isFiltered ? '' : undefined}
            data-filter-trigger={SEGMENT_FILTER_NAMES[AdminAnalyticsSegmentFilter.User]}
          >
            <UserIcon className="size-4" />
            <span className="max-w-[12rem] truncate">{triggerLabel}</span>
            <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
          </Headless.PopoverButton>
          <Headless.PopoverPanel
            transition
            anchor="bottom start"
            className={FILTER_PICKER_PANEL_CLASS}
          >
            <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Team member
            </div>
            {agencyUsers.length > 6 && (
              <div className="relative mb-1 px-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search team members"
                  className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 py-1.5 pl-8 pr-2 text-xs text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            )}
            <div className="max-h-72 overflow-y-auto">
              <button
                type="button"
                onClick={() => {
                  setAgencyUserId(undefined);
                  setQuery('');
                  close();
                }}
                className={clsx(
                  'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
                  !isFiltered
                    ? 'bg-primary-50 dark:bg-primary-500/10 font-medium text-primary-900 dark:text-primary-200'
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                )}
              >
                <span>{ALL_TEAM_LABEL}</span>
                {!isFiltered && (
                  <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                )}
              </button>
              {filtered.map((user) => {
                const selected = viewState.agencyUserId === user.id;
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      setAgencyUserId(user.id);
                      setQuery('');
                      close();
                    }}
                    className={clsx(
                      'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm',
                      selected
                        ? 'bg-primary-50 dark:bg-primary-500/10 font-medium text-primary-900 dark:text-primary-200'
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5'
                    )}
                  >
                    <div className="min-w-0 flex-1 text-left">
                      <div className="truncate">{user.name || user.email}</div>
                      {user.name && user.email !== user.name && (
                        <div className="truncate text-[11px] text-neutral-500 dark:text-neutral-400">
                          {user.email}
                        </div>
                      )}
                    </div>
                    {selected && (
                      <CheckIcon className="size-4 shrink-0 text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="px-2 py-3 text-center text-xs text-neutral-500 dark:text-neutral-400">
                  No matches
                </p>
              )}
            </div>
          </Headless.PopoverPanel>
        </>
      )}
    </Headless.Popover>
  );
};

export const AnalyticsFilterChips = (props: ReturnType<typeof useAnalyticsFilters>) => {
  const { viewState, segmentFiltersEnabled } = props;
  const { data: childData } = useGetChildAgenciesQuery();
  const childAgencies = childData?.agencyUser?.agency?.childAgencies ?? NO_CHILD_AGENCIES;
  const hasChildAgencies = childAgencies.length > 0;
  const agencyUsers = useAnalyticsAgencyUsers(
    effectiveSubagencyScope(viewState),
    childAgencies,
    viewState.agencyUserId,
    props.setAgencyUserId
  );
  const { profileOptions } = props;
  const showCSVExport = useFlag(FEATURE_FLAGS.ANALYTICS_CSV_EXPORT);

  const pickers = (
    <div className="flex flex-wrap items-center gap-2">
      <TimePeriodPicker {...props} />
      {hasChildAgencies && (
        <SubagencyPicker
          childAgencies={childAgencies}
          viewState={viewState}
          setSubagencyScope={props.setSubagencyScope}
        />
      )}
      {profileOptions && profileOptions.length > 0 && (
        <ProfilePicker
          options={profileOptions}
          selection={viewState.agencyProfile}
          onSelect={props.setAgencyProfile}
        />
      )}
      <TeamMemberPicker
        agencyUsers={agencyUsers}
        viewState={viewState}
        setAgencyUserId={props.setAgencyUserId}
      />
      {segmentFiltersEnabled && (
        <StartedByPicker startedBy={viewState.startedBy} onSelect={props.setStartedBy} />
      )}
      <div
        className="hidden h-6 w-px shrink-0 bg-neutral-200 dark:bg-neutral-700 sm:block"
        aria-hidden
        role="separator"
      />
      <AnalyticsAssumptionsPicker
        assumptions={props.assumptions}
        setHourlyRate={props.setHourlyRate}
        setAvgAccountPremium={props.setAvgAccountPremium}
      />
      {showCSVExport && <AnalyticsExportMenu filter={props.filter} />}
    </div>
  );
  if (!segmentFiltersEnabled) {
    return pickers;
  }

  const selectedUser = agencyUsers.find((u) => u.id === viewState.agencyUserId);
  // Pinned from lg up only: below it the page's h-16 mobile header is already sticky.
  return (
    <div className="space-y-2 lg:sticky lg:top-0 lg:z-20 lg:border-b lg:border-neutral-200 lg:bg-neutral-50 lg:py-3 lg:dark:border-neutral-700 lg:dark:bg-neutral-900">
      {pickers}
      <SegmentFilterChips
        filters={props}
        selectedUserLabel={selectedUser ? selectedUser.name || selectedUser.email : undefined}
      />
    </div>
  );
};
